import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { portraitThumbUrl } from "@/utils/portraitThumb";

const SUPABASE_URL =
  (import.meta.env.VITE_SUPABASE_URL as string | undefined) ||
  "https://mmnfjeukxandnhdaovzx.supabase.co";

// Protagonists (transmission ids) that have a looping motion-portrait video in
// the public `protagonist-portraits` Storage bucket (portrait_video_<id>.mp4).
const MOTION_AVATAR_IDS = new Set<number>([5, 8, 101, 108, 117, 124, 143]);

// Motion avatars that animate CONTINUOUSLY in the grid (not just on hover) — a
// small showcase. Every other grid avatar plays only while hovered, so at most
// ~one video decodes at a time. id 8 = A. Square (Flatland).
const ALWAYS_ANIMATE_IDS = new Set<number>([8]);

/**
 * AvatarImage that shows the portrait's small thumb, falling back to the
 * original if the thumb is missing, and only then to the AvatarFallback.
 * For avatars that don't need ProtagonistAvatar's motion video.
 */
export function PortraitAvatarImage({
  src,
  alt,
  className,
}: { src: string | null | undefined; alt: string; className?: string }) {
  const [thumbFailed, setThumbFailed] = useState(false);
  const thumb = portraitThumbUrl(src);
  if (!src) return null;
  return (
    <AvatarImage
      src={thumbFailed ? src : thumb ?? src}
      alt={alt}
      className={className}
      onLoadingStatusChange={(s) => {
        if (s === "error" && !thumbFailed && thumb !== src) setThumbFailed(true);
      }}
    />
  );
}

export function hasMotionAvatar(id?: number | null): boolean {
  return id != null && MOTION_AVATAR_IDS.has(id);
}

export function getMotionVideoUrl(id: number): string {
  return `${SUPABASE_URL}/storage/v1/object/public/protagonist-portraits/portrait_video_${id}.mp4`;
}

interface ProtagonistAvatarProps {
  /** Transmission id of the protagonist (selects the motion video, if any). */
  protagonistId?: number | null;
  portraitUrl?: string | null;
  name: string;
  className?: string;
  style?: CSSProperties;
  /**
   * Play the motion video continuously instead of only while hovered. Used for
   * single, focal avatars (e.g. voice mode); grid avatars leave this false so
   * only the hovered one plays. Forced on for the ALWAYS_ANIMATE_IDS showcase.
   */
  alwaysPlay?: boolean;
  /**
   * Pixel width of the resized portrait requested from storage. Small for list
   * and grid avatars; larger for a single focal portrait.
   */
  thumbWidth?: number;
}

/**
 * Protagonist avatar with an optional looping motion video, falling back to the
 * static portrait (or the name initial). Playback matches the approach that
 * worked reliably: an IntersectionObserver drives play()/pause() DIRECTLY (no
 * intermediate React state), gated so a video only plays while it is on-screen
 * AND (continuously-animated OR hovered). So at most ~one grid video runs.
 * Mounting is lazy (within ~400px of the viewport). Website-only.
 */
export function ProtagonistAvatar({
  protagonistId,
  portraitUrl,
  name,
  className,
  style,
  alwaysPlay = false,
  thumbWidth = 256,
}: ProtagonistAvatarProps) {
  // Thumb first; if it is missing (a portrait newer than the thumbs), fall back
  // to the original rather than to the initial.
  const [thumbFailed, setThumbFailed] = useState(false);
  // The initial is for a protagonist with no portrait, or one that will not
  // load. Shown while a portrait is still loading, it flashed on every card.
  const [imageFailed, setImageFailed] = useState(false);
  const thumbSrc = portraitThumbUrl(portraitUrl, thumbWidth);
  const imageSrc = thumbFailed ? portraitUrl ?? null : thumbSrc;
  const [videoFailed, setVideoFailed] = useState(false);
  const [near, setNear] = useState(false);
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hoveredRef = useRef(false);
  const onScreenRef = useRef(false);

  const isMotion = hasMotionAvatar(protagonistId) && !videoFailed;
  const animateAlways =
    alwaysPlay || (protagonistId != null && ALWAYS_ANIMATE_IDS.has(protagonistId));

  // Set the video ref AND force the muted *property* on. React's `muted` prop
  // only sets the attribute reliably, not the property — and the browser's
  // autoplay policy checks the property when script calls play(), so without
  // this, play() is blocked (which is why the grid didn't animate but the
  // autoPlay-attribute expanded view did).
  const setVideoRef = (el: HTMLVideoElement | null) => {
    videoRef.current = el;
    if (el) el.muted = true;
  };

  // Play iff on-screen AND (continuously-animated OR hovered); otherwise pause.
  // Called directly from the observer, the hover handlers, and once the video's
  // data has loaded — never gated behind React state (which is what broke it).
  const syncPlayback = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true; // ensure the property is set before every play() attempt
    if (onScreenRef.current && (animateAlways || hoveredRef.current)) {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } else {
      try { v.pause(); } catch { /* ignore */ }
    }
  };

  // Lazy-mount the image/video once the card nears the viewport, then keep it.
  useEffect(() => {
    if (near) return;
    const el = containerRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setNear(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          obs.disconnect();
        }
      },
      { rootMargin: "400px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [near]);

  // Track on-screen visibility and drive playback directly from the observer.
  useEffect(() => {
    if (!isMotion || !near) return;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      onScreenRef.current = true;
      syncPlayback();
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        onScreenRef.current = entries.some((e) => e.isIntersecting);
        syncPlayback();
      },
      { rootMargin: "0px", threshold: 0.1 },
    );
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMotion, near, animateAlways]);

  return (
    <Avatar
      ref={containerRef}
      className={className}
      style={style}
      onMouseEnter={() => { hoveredRef.current = true; syncPlayback(); }}
      onMouseLeave={() => { hoveredRef.current = false; syncPlayback(); }}
    >
      {isMotion && near && (
        <video
          ref={setVideoRef}
          key={protagonistId}
          src={getMotionVideoUrl(protagonistId as number)}
          poster={imageSrc ?? undefined}
          autoPlay={animateAlways}
          loop
          muted
          playsInline
          preload="auto"
          onError={() => setVideoFailed(true)}
          onLoadedData={syncPlayback}
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      {near && imageSrc ? (
        <AvatarImage
          src={imageSrc}
          alt={name}
          loading="lazy"
          decoding="async"
          className="object-cover"
          onLoadingStatusChange={(s) => {
            if (s !== "error") return;
            if (!thumbFailed && thumbSrc !== portraitUrl) setThumbFailed(true);
            else setImageFailed(true);
          }}
        />
      ) : null}
      <AvatarFallback className="bg-muted text-muted-foreground text-3xl">
        {!portraitUrl || imageFailed ? name.charAt(0) : null}
      </AvatarFallback>
    </Avatar>
  );
}
